import {
  Button,
  CircularProgress,
  type ButtonProps,
  type SxProps,
  type Theme,
} from "@mui/material";
import type {
  ElementType,
  FC,
  InputHTMLAttributes,
  MouseEventHandler,
} from "react";
import styled from "styled-components";

import type {
  IStyledAttributes,
  MuiPaletteType,
  MuiSize,
  MuiVariant,
} from "@ryde/shared";

interface IMuiButton extends InputHTMLAttributes<HTMLButtonElement> {
  component?: ElementType;
  title?: string;
  disabled?: boolean;
  loading?: boolean;
  sx?: SxProps<Theme>;
  muiStyle?: MuiPaletteType;
  muiSize?: MuiSize;
  muiVariant?: MuiVariant;
  onClick?: MouseEventHandler<HTMLElement>;
  className?: string;
  href?: string;
  e2eData?: string;
  startIcon?: ButtonProps["startIcon"];
  endIcon?: ButtonProps["endIcon"];
}

const ButtonContentStyled = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "button-content",
})`
  display: flex;
  align-items: center;
  margin-top: 0;
  margin-bottom: 0;
  padding: 0;
  /* min-, so a label that wraps grows the button instead of spilling over its edges */
  min-height: 26px;
  font-weight: 400;
  font-size: 14px;
  text-transform: none;
  gap: 10px;
`;

export const MuiButton: FC<IMuiButton> = ({
  component,
  children,
  title,
  onClick,
  disabled,
  loading,
  sx,
  muiStyle = "primary",
  muiSize = "small",
  muiVariant = "contained",
  className,
  href,
  e2eData = "mui-button-ordinary",
  startIcon,
  endIcon,
}) => {
  return (
    <Button
      component={component || "button"}
      variant={muiVariant}
      color={muiStyle}
      size={muiSize}
      onClick={onClick}
      disabled={disabled || loading}
      title={title}
      sx={sx}
      data-e2e={e2eData}
      {...(className && { className })}
      {...(href && { href })}
      startIcon={startIcon}
      endIcon={endIcon}
    >
      <ButtonContentStyled data-e2e-custom={`content_${e2eData}`}>
        {children}
        {Boolean(loading) && <CircularProgress sx={{ ml: "6px" }} size={12} />}
      </ButtonContentStyled>
    </Button>
  );
};
